import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

function UsersList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('All Users');
  const navigate = useNavigate();
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    if (stored.role !== 'super_admin') {
      navigate('/');
    }
  }, [navigate]);
  useEffect(() => {
    fetch('http://127.0.0.1:8000/api/users/list', { headers: getAuthHeaders() })
      .then(res => {
        if (res.status === 401 || res.status === 403) {
          navigate('/login');
          throw new Error('Not authorized');
        }
        if (!res.ok) throw new Error('Failed to fetch users');
        return res.json();
      })
      .then(data => {
        setUsers(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [navigate]);

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'super_admin':
        return { backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' };
      case 'holding_admin':
        return { backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' };
      case 'company_admin':
        return { backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' };
      default:
        return { backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' };
    }
  };

  const formatRole = (role) => {
    if (!role) return 'No Role';
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const tabBtnStyle = (isActive) => ({
    padding: '8px 16px',
    borderRadius: '999px',
    border: isActive ? 'none' : '1px solid #cbd5e1',
    backgroundColor: isActive ? '#2563eb' : '#fff',
    color: isActive ? '#fff' : '#64748b',
    fontSize: '0.85rem',
    fontWeight: isActive ? '600' : '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  });

  // Filter by tab
  let filteredUsers = users;
  if (activeTab === 'Super Admins') {
    filteredUsers = filteredUsers.filter(u => u.role === 'super_admin');
  } else if (activeTab === 'Holding Admins') {
    filteredUsers = filteredUsers.filter(u => u.role === 'holding_admin');
  } else if (activeTab === 'Company Admins') {
    filteredUsers = filteredUsers.filter(u => u.role === 'company_admin');
  }

  // Filter by search
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filteredUsers = filteredUsers.filter(user =>
      user.name.toLowerCase().includes(term) ||
      (user.email || '').toLowerCase().includes(term) ||
      (user.phone || '').toLowerCase().includes(term) ||
      (user.role && user.role.toLowerCase().includes(term)) ||
      formatRole(user.role).toLowerCase().includes(term)
    );
  }

  if (initialLoading) return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading Users...</div>;
  if (error) return <div style={{ color: 'red', padding: '20px' }}>Error: {error}</div>;

  return (
    <div style={{ padding: '40px' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0' }}>Users</h1>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
          {users.length} total user{users.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px 20px' }}>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Total Users</h3>
          <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#0f172a' }}>{users.length}</p>
        </div>
        <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px 20px' }}>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Super Admins</h3>
          <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#92400e' }}>{users.filter(u => u.role === 'super_admin').length}</p>
        </div>
        <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px 20px' }}>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Holding Admins</h3>
          <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#1e40af' }}>{users.filter(u => u.role === 'holding_admin').length}</p>
        </div>
        <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px 20px' }}>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Company Admins</h3>
          <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#065f46' }}>{users.filter(u => u.role === 'company_admin').length}</p>
        </div>
      </div>

      {/* Filter Tabs + Search */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button style={tabBtnStyle(activeTab === 'All Users')} onClick={() => setActiveTab('All Users')}>All Users</button>
          <button style={tabBtnStyle(activeTab === 'Super Admins')} onClick={() => setActiveTab('Super Admins')}>Super Admins</button>
          <button style={tabBtnStyle(activeTab === 'Holding Admins')} onClick={() => setActiveTab('Holding Admins')}>Holding Admins</button>
          <button style={tabBtnStyle(activeTab === 'Company Admins')} onClick={() => setActiveTab('Company Admins')}>Company Admins</button>
        </div>

        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '1rem', color: '#94a3b8' }}>🔍</span>
          <input
            type="text"
            placeholder="Search by name, phone, email, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '300px',
              padding: '10px 14px 10px 38px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '0.9rem',
              outline: 'none',
              backgroundColor: '#fff',
              color: '#0f172a',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#2563eb'}
            onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
          />
        </div>
      </div>

      {/* Result count when filtering */}
      {(searchTerm || activeTab !== 'All Users') && (
        <p style={{ margin: '0 0 12px 0', fontSize: '0.82rem', color: '#64748b' }}>
          Showing {filteredUsers.length} of {users.length} users
        </p>
      )}

      {/* Users Table */}
      <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.5fr 1fr 0.8fr',
          padding: '12px 20px',
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          fontSize: '0.8rem',
          fontWeight: '600',
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          <span>Name</span>
          <span>Email / phone</span>
          <span>Role</span>
          <span>Joined</span>
        </div>

        {/* User Rows */}
        {filteredUsers.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontStyle: 'italic' }}>
            {searchTerm ? 'No users match your search.' : 'No users found in this category.'}
          </div>
        ) : (
          filteredUsers.map(user => (
            <div
              key={user.id}
              onClick={() => navigate(`/users/${user.id}`)}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1.5fr 1fr 0.8fr',
                padding: '14px 20px',
                borderBottom: '1px solid #f1f5f9',
                cursor: 'pointer',
                transition: 'background 0.15s',
                alignItems: 'center'
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: '#2563eb',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  flexShrink: 0
                }}>
                  {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                </div>
                <span style={{ fontWeight: '600', color: '#0f172a', fontSize: '0.95rem' }}>{user.name}</span>
              </div>
              <span style={{ color: '#64748b', fontSize: '0.9rem' }}>{user.email || user.phone || '—'}</span>
              <span>
                <span style={{
                  ...getRoleBadgeStyle(user.role),
                  padding: '4px 10px',
                  borderRadius: '999px',
                  fontSize: '0.78rem',
                  fontWeight: '600',
                  display: 'inline-block'
                }}>
                  {formatRole(user.role)}
                </span>
              </span>
              <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default UsersList;
